import { useMutation, useQuery } from "@apollo/client/react";
import {
  CreateCategoryDocument,
  DeleteCategoryDocument,
  MyCategoriesDocument,
  MyStoreDocument,
  UpdateCategoryDocument,
} from "@/generated/graphql";
import { graphQLErrorMessage } from "@/lib/graphqlErrors";
import type { Category, CategoryInput } from "@/types/models";

export function useCategories() {
  const { data: storeData, loading: storeLoading } = useQuery(MyStoreDocument, {
    fetchPolicy: "network-only",
  });

  const { data, loading, error, refetch } = useQuery(MyCategoriesDocument, {
    fetchPolicy: "network-only",
    skip: !storeData?.myStore,
  });

  const categories: Category[] = data?.myCategories ?? [];
  const store = storeData?.myStore ?? null;

  const [createCategoryMutation, { loading: creating }] = useMutation(CreateCategoryDocument, {
    refetchQueries: [{ query: MyCategoriesDocument }],
  });

  const [updateCategoryMutation, { loading: updating }] = useMutation(UpdateCategoryDocument, {
    refetchQueries: [{ query: MyCategoriesDocument }],
  });

  const [deleteCategoryMutation, { loading: deleting }] = useMutation(DeleteCategoryDocument, {
    refetchQueries: [{ query: MyCategoriesDocument }],
  });

  const createCategory = (
    input: CategoryInput,
    callbacks?: { onCompleted?: () => void; onError?: (message: string) => void }
  ) => {
    createCategoryMutation({
      variables: { input },
      onCompleted: () => callbacks?.onCompleted?.(),
      onError: (err) => {
        callbacks?.onError?.(graphQLErrorMessage(err, "Não foi possível criar a categoria."));
      },
    });
  };

  const updateCategory = (
    input: CategoryInput,
    callbacks?: { onCompleted?: () => void; onError?: (message: string) => void }
  ) => {
    updateCategoryMutation({
      variables: { input },
      onCompleted: () => callbacks?.onCompleted?.(),
      onError: (err) => {
        callbacks?.onError?.(graphQLErrorMessage(err, "Não foi possível atualizar a categoria."));
      },
    });
  };

  const deleteCategory = (
    id: string,
    callbacks?: { onCompleted?: () => void; onError?: (message: string) => void }
  ) => {
    deleteCategoryMutation({
      variables: { id },
      onCompleted: () => callbacks?.onCompleted?.(),
      onError: (err) => {
        callbacks?.onError?.(graphQLErrorMessage(err, "Não foi possível excluir a categoria."));
      },
    });
  };

  return {
    store,
    categories,
    loading: storeLoading || loading,
    error,
    refetch,
    createCategory,
    updateCategory,
    deleteCategory,
    saving: creating || updating,
    deleting,
  };
}
